import { createParagraph } from '@lumapps/wrex-typography/utils/createParagraph';
import { isDivider } from '@lumapps/wrex-typography/utils/isDivider';
import { isParagraph } from '@lumapps/wrex-typography/utils/isParagraph';
import { Editor, Node, Path, Transforms, type NodeEntry } from '@lumapps/wrex/slate';
import { isElement } from '@lumapps/wrex/slate/utils/isElement';
import { isElementType } from '@lumapps/wrex/slate/utils/isElementType';
import { normalizeLiftToRootNode } from '@lumapps/wrex/slate/utils/normalizeLiftToRootNode';
import type { Wrex, WrexEditor } from '@lumapps/wrex/types';

import type { ColumnsEditor } from '../../types';
import { isColumn } from '../../utils/isColumn';
import { isColumnEmpty } from '../../utils/isColumnEmpty';
import { isColumns } from '../../utils/isColumns';

const isColumnChildAllowed = (node: any, allowedElements: string[]): boolean => allowedElements.includes(node.type);

export const normalizeColumn = (
    [node, path]: NodeEntry,
    editor: WrexEditor<ColumnsEditor>,
    allowedElements: string[],
) => {
    if (!isElement(node)) {
        return false;
    }
    const [parentNode, parentPath] = Editor.parent(editor, path);

    // We first remove node not allowed in column
    if (isColumn(parentNode) && !isColumn(node) && !isColumnChildAllowed(node, allowedElements)) {
        Editor.withoutNormalizing(editor, () => {
            normalizeLiftToRootNode(editor, [node, path], isElementType((node as Wrex.Element).type), {
                mode: 'after',
            });
            if (isColumnEmpty(editor, parentPath)) {
                // we make sure the current column node isn't empty
                Transforms.insertNodes(editor, createParagraph(), {
                    at: [...parentPath, 0],
                });
            }
        });
        return true;
    }

    if (isColumn(node)) {
        // if a column doesn't have "columns" as parent we unwrap it
        if (!isColumns(parentNode)) {
            Transforms.unwrapNodes(editor, { at: path });
            return true;
        }

        // a column node cannot exist without a text node child
        // so we make sure that there's always at least a paragraph
        if (node.children.length === 0) {
            Transforms.insertNodes(editor, createParagraph(), {
                at: [...path, 0],
            });
            return true;
        }
    }
    // we make sure that dividers are normalized as outside columns with a paragraph after
    if (isDivider(node) && isColumn(parentNode)) {
        const nextPath = Path.next(path);
        const nextNode = Node.has(editor, nextPath) ? Node.get(editor, nextPath) : null;

        if (!nextNode || !isParagraph(nextNode)) {
            Transforms.insertNodes(editor, createParagraph(), { at: nextPath });
            return true;
        }
    }

    return false;
};
